import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signIn } from "../auth/client";
import { BrandLogo } from "../components/BrandLogo";

interface InviteData {
  email: string;
  name: string;
  permissions: string[];
  invitedBy: string;
}

export function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Verify the invite token on mount
  useEffect(() => {
    async function verifyInvite() {
      if (!token) {
        setError("No invite token provided");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`/api/invites/verify/${token}`);
        const data = await res.json();

        if (!data.success) {
          setError(data.error || "Invalid invite");
          setLoading(false);
          return;
        }

        setInviteData(data.data);
        setLoading(false);
      } catch {
        setError("Failed to verify invite");
        setLoading(false);
      }
    }

    verifyInvite();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setSubmitting(true);

    try {
      // Accept the invite and set password
      const acceptRes = await fetch("/api/invites/accept", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      const acceptData = await acceptRes.json();

      if (!acceptData.success) {
        setError(acceptData.error || "Failed to activate account");
        setSubmitting(false);
        return;
      }

      // Auto sign in
      const signInResult = await signIn.email({
        email: inviteData!.email,
        password,
      });

      if (signInResult.error) {
        // Account was created but auto-login failed, redirect to login
        navigate("/login?activated=true");
        return;
      }

      // Success - redirect to dashboard
      navigate("/");
    } catch {
      setError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-2">
            <BrandLogo variant="primary" className="h-12 w-auto" />
            <BrandLogo variant="wordmark" className="h-8 w-auto" />
          </div>
          <div className="w-8 h-8 border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Verifying invite...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid/expired token)
  if (error && !inviteData) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
        <div className="flex items-center gap-2 mb-8">
          <BrandLogo variant="primary" className="h-12 w-auto" />
          <BrandLogo variant="wordmark" className="h-8 w-auto" />
        </div>

        <div className="w-full max-w-md bg-card rounded-3xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Invalid Invite</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button
            onClick={() => navigate("/login")}
            className="w-full py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] hover:opacity-90 transition-opacity"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      <div className="flex items-center gap-2 mb-8">
        <BrandLogo variant="primary" className="h-12 w-auto" />
        <BrandLogo variant="wordmark" className="h-8 w-auto" />
      </div>

      <div className="w-full max-w-md bg-card rounded-3xl shadow-xl border border-border p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Welcome, {inviteData?.name}!</h1>
          <p className="text-muted-foreground">
            Set your password to join the dashboard
          </p>
          {inviteData?.invitedBy && (
            <p className="text-sm text-muted-foreground/70 mt-2">
              Invited by {inviteData.invitedBy}
            </p>
          )}
        </div>

        {/* Email display (read-only) */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-2">
            Email
          </label>
          <div className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground">
            {inviteData?.email}
          </div>
        </div>

        {/* Password form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a password"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/20 outline-none transition-all"
              required
              minLength={8}
            />
            <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary)]/20 outline-none transition-all"
              required
              minLength={8}
            />
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Activating Account...
              </span>
            ) : (
              "Activate Account"
            )}
          </button>
        </form>

        {/* Permissions preview */}
        {inviteData?.permissions && inviteData.permissions.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Your Access
            </p>
            <div className="flex flex-wrap gap-2">
              {inviteData.permissions.map((perm) => (
                <span
                  key={perm}
                  className="px-3 py-1 text-xs font-medium bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] rounded-full capitalize"
                >
                  {perm.replace("-", " ")}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="mt-8 text-sm text-muted-foreground">
        By activating, you agree to our Terms of Service and Privacy Policy
      </p>
    </div>
  );
}
